import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailablePerformancePointReport,
  RequestReportInterFace,
} from '../../../../interface/reportPerformancePoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchPerformanceReportPoint,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setPerformancePointReportUpdate,
  setSortData,
  refreshPerformanceReportPoint,
} from '../../../../redux/slices/ReportPerformancePointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * PerformanceReportTablePoint component
 * @param {any} properties - The properties
 */
function PerformanceReportTablePoint(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isPerformancePointReportUpdate,
  } = useAppSelector((state) => state.reportPerformancePoint);
  const dispatch = useAppDispatch();
  const { sortBy, sortField } = useAppSelector((state) => state.reportPerformancePoint);
  const reportsData = useAppSelector((state) => state.reportData);
  /**
   * Fetches energy report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchPerformanceReportPoint({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPerformanceReportPoint({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent, handleOpenStationSidePanel  } =
   
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: reports,
      disableEdit: true,
      disableDelete: true,
    });
  const column = [
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailablePerformancePointReport,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailablePerformancePointReport,
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Station info view
       * @param {any} report - the performance report data
       */
      customData: (report: AvailablePerformancePointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => { event.stopPropagation(); handleOpenStationSidePanel(report); }}
            >
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sortAllow: false,
      columnWidth: 204,
    },
    {
      label: t('freeUsage'),
      accessorKey: 'freeSessionCount' as keyof AvailablePerformancePointReport,
      sortAllow: true,
    },
    {
      label: t('paidUsage'),
      accessorKey: 'paidSessionCount' as keyof AvailablePerformancePointReport,
      sortAllow: true,
    },
  ];

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Auto refresh devices
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshPerformanceReportPoint({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        accountCustomId: reportsData?.accountId,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Current columns for Performance Point Report export
   */
  const currentColumns = [
    { label: 'SNo', accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('freeUsage'), accessorKey: 'freeSessionCount' },
    { label: t('paidUsage'), accessorKey: 'paidSessionCount' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchPerformancePointListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailablePerformancePointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'freeSessionCount':
                return row?.freeSessionCount ?? '0';

              case 'paidSessionCount':
                return row?.paidSessionCount ?? '0';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters for fetching the report
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isPerformancePointReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setPerformancePointReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isPerformancePointReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter?.pageName === 'performanceReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter?.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div>
      <CustomTable
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={handleApiRequest}
        version="v2"
        height={370}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        filters={filters}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        refershCurrentPages={ReferenceCurrentPages}
        sizeChangeButtonRequired
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        serialNo
        onHiddenColumnsChange={setHiddenColumns}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        rightTopContent={rightTopContent}
      />
    </div>
  );
}

export default PerformanceReportTablePoint;
